import { createContext, useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  setAssumedRoles,
  PROJECT_ASSUME_SCOPE,
} from '../../../api/assumedRoles';
import { bookingKeys } from '../../../api/bookingQueries';

/*
 * Projects are "unassumed": their list is visible, but everything below
 * a project (booking items, hosting assets) only becomes accessible
 * after assuming the project admin role. Several projects can be
 * entered at the same time — their roles are sent semicolon-separated
 * and only apply to booking/hosting requests (see assumedRoles.js).
 * The assumption is deliberately not persisted — a reload starts
 * unassumed again.
 */
const AssumedRoleContext = createContext(null);

/* the role entering a project assumes — also the checkbox tooltip */
export const projectAdminRole = (project) =>
  `hs_booking.project#${project.uuid}:ADMIN`;

export function AssumedRoleProvider({ children }) {
  const queryClient = useQueryClient();
  const [assumedProjects, setAssumedProjects] = useState([]);

  const applyAssumption = (projects) => {
    setAssumedRoles(
      PROJECT_ASSUME_SCOPE,
      projects.length ? projects.map(projectAdminRole).join(';') : null
    );
    setAssumedProjects(projects);
  };

  const assumeProject = (project) => {
    applyAssumption([
      ...assumedProjects.filter((entered) => entered.uuid !== project.uuid),
      project,
    ]);
  };

  const leaveProject = (project) => {
    applyAssumption(
      assumedProjects.filter((entered) => entered.uuid !== project.uuid)
    );
    queryClient.removeQueries({ queryKey: bookingKeys.items(project.uuid) });
  };

  return (
    <AssumedRoleContext.Provider
      value={{ assumedProjects, assumeProject, leaveProject }}
    >
      {children}
    </AssumedRoleContext.Provider>
  );
}

export function useAssumedRole() {
  const context = useContext(AssumedRoleContext);
  if (!context) {
    throw new Error('useAssumedRole requires an AssumedRoleProvider');
  }
  return context;
}
